import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import { styled } from '@mui/material';
import { Checkbox } from '@calefy-inc/informedMaterial';
import { CoreWhiteLabelInsuranceTypeTypeChoices } from '../../../../../../gql/graphql';
import {
  WhiteLabelSectionDivider,
  WhiteLabelSectionRoot,
} from './whiteLabelSectionCommon';

/** Form field name for the only behavioural flag we expose to Policystream V1-only orgs. */
const BEHAVIOURAL_FLAG_SHOWN_FOR_POLICYSTREAM_ONLY = 'includeCustomShadows';

export type WhiteLabelBehaviouralFlagRow = {
  name: string;
  label: string;
};

export interface FlagsSectionProps {
  /** From GraphQL whiteLabelBehaviouralFlagDefinitions (codegen may allow null list items). */
  definitions: ReadonlyArray<WhiteLabelBehaviouralFlagRow | null | undefined>;
  /**
   * Same WL row as the form (management or public). Client `useSettings().types` reflects the
   * admin site domain, not the org selected in the backend — use this for flag visibility.
   */
  whiteLabelInsuranceTypes: ReadonlyArray<
    { readonly type: CoreWhiteLabelInsuranceTypeTypeChoices } | null | undefined
  >;
}

/** Policystream V1-only WL matches strict PolicyStream behaviour (see useIsPolicyStreamOrganization). */
function isStrictPolicyStreamWhiteLabel(
  types: FlagsSectionProps['whiteLabelInsuranceTypes'],
): boolean {
  const codes = types
    .map((t) => t?.type)
    .filter((x): x is CoreWhiteLabelInsuranceTypeTypeChoices => x != null);
  return (
    codes.includes(CoreWhiteLabelInsuranceTypeTypeChoices.Life) &&
    codes.length === 1
  );
}

export function FlagsSection({
  definitions,
  whiteLabelInsuranceTypes,
}: FlagsSectionProps) {
  const isPolicyStreamOnlyOrganization = isStrictPolicyStreamWhiteLabel(
    whiteLabelInsuranceTypes,
  );
  const rows = definitions.filter(
    (d): d is WhiteLabelBehaviouralFlagRow => d != null,
  );
  const visibleRows = isPolicyStreamOnlyOrganization
    ? rows.filter(
        (r) => r.name === BEHAVIOURAL_FLAG_SHOWN_FOR_POLICYSTREAM_ONLY,
      )
    : rows;

  return (
    <WhiteLabelSectionRoot>
      <Typography variant='h6' gutterBottom>
        Feature Flags
      </Typography>
      <WhiteLabelSectionDivider />

      <FlagsList>
        {visibleRows.map(({ name, label }) => (
          <FormControlLabel
            key={name}
            control={<Checkbox name={name} />}
            label={label}
          />
        ))}
      </FlagsList>
    </WhiteLabelSectionRoot>
  );
}

/** Vertical stack of flag checkboxes */
const FlagsList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));
